import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, map, race, switchMap, take, timer } from 'rxjs';
import { AuthStore } from '../store/auth.store';
import { logAuthSession } from '../auth/auth-session-log';

/**
 * Backstop for the wait below (CF-03). If bootstrap never hydrates (an exception in its chain, a
 * browser without `crypto.subtle`), every guarded route used to wait forever behind the loader.
 * After this long the guard hydrates from whatever storage holds (anonymous if nothing is
 * readable) and lets routing proceed.
 *
 * The clock starts when bootstrap starts (`AuthStore.bootstrapStarted`), not when the guard
 * starts waiting: the browser/install gate holds the initial navigation for as long as the user
 * stays on it, and firing then would hydrate before storage is read and route a signed-in user
 * to Login. Kept well above the slowest legitimate startup — CMS wait (4s), then up to two
 * refreshes under the cross-window lock, each with its own CMS wait (4s) and timeout (15s).
 */
export const AUTH_HYDRATION_TIMEOUT_MS = 60_000;

/**
 * Awaits the bootstrap hydration kicked off in `app.ts`'s `ngOnInit`
 * (`LocalStorageManagerService.hydrate()` -> silent token refresh ->
 * `AuthStore.hydrate()`) before letting a guard decide anything based on
 * `AuthStore`'s state — shared by every guard that reads `isAuthenticated`,
 * `guestToken`/`accessToken`, or (transitively) anything cached in
 * `LocalStorageManagerService` (profile picture, display name, etc.).
 *
 * Extracted from `root-redirect.guard.ts`, which already used this exact
 * pattern, after finding the same wait was *missing* from `protectedGuard`
 * and `guestGuard` — those two returned/decided immediately whenever
 * `isLoading()` was still `true` (its default value on a cold load),
 * letting Dashboard/Profile/Login mount before storage hydration finished.
 * At that moment `LocalStorageManagerService.getProfilePicture()` /
 * `getFirstName()` are still empty, so the avatar's `name` input falls back
 * to a literal placeholder string (still non-empty) while `storedPicture`
 * is empty — `ProfileAvatarComponent` reads that as "no real photo, but we
 * have *some* identity" and renders its generic SVG placeholder tier, which
 * then flips to the real picture a beat later once hydration completes.
 * That's the "SVG placeholder flashes before the real avatar loads" bug —
 * fixed at the root by making sure no guarded route ever activates before
 * hydration is done, so `LocalStorageManagerService` already has the right
 * cached values on the very first render everywhere.
 */
export async function waitForAuthHydration(authStore: AuthStore): Promise<void> {
  if (!authStore.isLoading()) return;

  const hydrated = await firstValueFrom(
    race(
      toObservable(authStore.isLoading).pipe(
        filter((loading) => !loading),
        take(1),
        map(() => true),
      ),
      toObservable(authStore.bootstrapStarted).pipe(
        filter(Boolean),
        take(1),
        switchMap(() => timer(AUTH_HYDRATION_TIMEOUT_MS)),
        map(() => false),
      ),
    ),
  );
  if (!hydrated && authStore.isLoading()) {
    logAuthSession('auth-hydration-timeout', 'error', { waitedMs: AUTH_HYDRATION_TIMEOUT_MS });
    authStore.hydrate();
  }
}
